import { useCallback, useEffect, useState } from 'react';
import { Filesystem } from '@capacitor/filesystem';
import { useToast } from '@chakra-ui/react';
import { decode } from 'base64-arraybuffer';
import { isEmpty } from 'lodash';
import { useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { BillUploadException, BillUploadResult, UploadURLResult } from 'clipsal-cortex-types/src/api/api-bills';

import { NetworkError, post } from '../../../../common/api/api-helpers';
import { selectSite } from '../../../site/siteSlice';
import { useGetSiteBillsQuery } from '../../billsApi';
import { BillUploadStatus } from './upload-bill-helpers';

export type UploadBillsProps = { onResetBills: () => void; billUrl?: string | null; onClose: () => void };

export type BillToUpload = {
  fileName: string;
  status: BillUploadStatus;
};

const ERROR_TOAST_TIMEOUT_MS = 10_000;
const MAX_FILE_SIZE_BYTES = 50_000_000;

export const useUploadBill = ({ onResetBills, onClose, billUrl }: UploadBillsProps) => {
  // The upload states will contain parallel indices with uploaded bills
  const [uploadedBill, setUploadedBill] = useState<BillToUpload>({
    fileName: '',
    status: BillUploadStatus.InProgressPreSigning,
  });
  const toast = useToast();
  const site = useSelector(selectSite);
  const [isUploading, setIsUploading] = useState(false);
  const [failedBillS3Path, setFailedBillS3Path] = useState('');
  const { data: bills } = useGetSiteBillsQuery(site.site_id);
  const [search] = useSearchParams();
  const navigate = useNavigate();

  const uploadBill = useCallback(
    async (name: string, pdfArrayBuffer: ArrayBuffer) => {
      const bill = {
        fileName: name,
        status: BillUploadStatus.InProgressPreSigning,
      };
      setUploadedBill(bill);

      let s3Path: string;
      try {
        const {
          signed_url: signedUrl,
          form_fields: formFields,
          plain_path: plainPath,
        } = await post<UploadURLResult>(`/v1/upload_url`, {
          file_extension: 'pdf',
          max_file_size: MAX_FILE_SIZE_BYTES,
          base_path: `bills/${site.site_id}`,
        });
        s3Path = plainPath;
        setUploadedBill({ ...bill, status: BillUploadStatus.InProgressUploading });

        const formData = new FormData();
        for (const key in formFields) {
          formData.append(key, formFields[key]);
        }
        formData.append('file', new Blob([pdfArrayBuffer], { type: formFields['Content-Type'] }));
        // Save to each presigned url
        await fetch(signedUrl, {
          method: 'POST',
          body: formData,
        });
        setUploadedBill({ ...bill, status: BillUploadStatus.InProgressProcessing });
      } catch {
        setUploadedBill({ ...bill, status: BillUploadStatus.FailGeneric });
        return;
      }
      try {
        const result = await post<BillUploadResult>(`/v2/sites/${site.site_id}/upload_bill`, {
          bill_s3_path: s3Path,
        });
        const mostRecentBill = bills![0];
        let status = BillUploadStatus.Success;
        if (result?.bill_start_date && mostRecentBill?.start_date > result.bill_start_date) {
          status = BillUploadStatus.SuccessHistorical;
        }
        onResetBills();
        setUploadedBill({ ...bill, status });
      } catch (e) {
        let billUploadStatus;
        const errorData = (e as NetworkError)?.response?.data as BillUploadException;
        if (errorData.code === 'UNSUPPORTED') billUploadStatus = BillUploadStatus.FailUnsupported;
        else if (errorData.code === 'GENERIC') billUploadStatus = BillUploadStatus.FailGeneric;
        else billUploadStatus = BillUploadStatus.TentativeFail;
        setUploadedBill({ ...bill, status: billUploadStatus });
        setFailedBillS3Path(s3Path);
      }
    },
    [bills, onResetBills, site.site_id]
  );

  /* istanbul ignore next -- @preserve */
  const readFileFromUrl = useCallback(
    async (title: string) => {
      if (!billUrl) return;
      const { data: pdf } = await Filesystem.readFile({
        path: billUrl,
      });
      // remove search params from url as reloading site can start upload bill process again
      if (search.has('bill-url')) {
        search.delete('bill-url');
        if (search.has('bill-title')) search.delete('bill-title');
        navigate(
          {
            search: search.toString(),
          },
          { replace: true }
        );
      }
      setIsUploading(true);
      await uploadBill(title, decode(pdf as string));
    },
    [billUrl, navigate, search, uploadBill]
  );

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (!isEmpty(acceptedFiles)) {
        setIsUploading(true);
      }

      acceptedFiles.forEach((file) => {
        const bufferReader = new FileReader();

        bufferReader.onabort = () => {
          toast({
            title: 'Error reading file.',
            description: 'Please try uploading this file again.',
            status: 'error',
            duration: ERROR_TOAST_TIMEOUT_MS,
            isClosable: true,
          });
          setIsUploading(false);
          console.log('file reading was aborted');
        };

        bufferReader.onerror = () => {
          toast({
            title: 'Error reading file.',
            description: 'Please try uploading this file again.',
            status: 'error',
            duration: ERROR_TOAST_TIMEOUT_MS,
            isClosable: true,
          });
          setIsUploading(false);
          console.log('file reading has failed');
        };

        bufferReader.onload = async () => {
          if (file.size > MAX_FILE_SIZE_BYTES) {
            toast({
              title: 'Error reading file.',
              description: 'File is over 50MB, too large.',
              status: 'error',
              duration: ERROR_TOAST_TIMEOUT_MS,
              isClosable: true,
            });
            setIsUploading(false);
            return;
          }
          await uploadBill(file.name, bufferReader.result as ArrayBuffer);
        };
        bufferReader.readAsArrayBuffer(file);
      });
    },
    [toast, uploadBill]
  );

  /* istanbul ignore next -- @preserve */
  useEffect(() => {
    if (billUrl) {
      const title = search.get('bill-title') || 'New Bill';
      readFileFromUrl(title);
    }
  }, [billUrl, search, readFileFromUrl]);

  const handleGetActionFromStatus = useCallback(() => {
    const failureStatuses = [BillUploadStatus.FailUnsupported, BillUploadStatus.FailGeneric];
    const billUploadFailed = failureStatuses.includes(uploadedBill.status);

    if (billUploadFailed) setIsUploading(false);
    else onClose();
  }, [onClose, uploadedBill]);

  const onFinishManualBillUpload = useCallback(() => {
    setFailedBillS3Path('');
    // show success screen when manual upload is finished
    setUploadedBill({ ...uploadedBill, status: BillUploadStatus.Success });
  }, [uploadedBill]);

  return {
    isUploading,
    uploadedBill,
    handleGetActionFromStatus,
    failedBillS3Path,
    onFinishManualBillUpload,
    onDrop,
  };
};
